import { forwardRef } from 'react';
import { useHoverIntent, type UseHoverIntentOptions } from '@/hooks/use-hover-intent';
import type { ComponentPropsWithoutRef, Ref } from 'react';

type HoverIntentGroupProps = ComponentPropsWithoutRef<'div'> & {
    hoverIntent?: UseHoverIntentOptions;
    /**
     * Second ref sink, for libraries that hand one back through props
     * (dnd's `provided.innerRef`) while a parent still wants its own.
     */
    innerRef?: Ref<HTMLDivElement>;
};

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
    if (typeof ref === 'function') ref(value);
    else if (ref && typeof ref === 'object') (ref as { current: T | null }).current = value;
}

/**
 * Group root for hover-revealed chrome. Stamps `data-hover-intent="on"` once
 * the pointer has settled, so children can gate off
 * `group-data-[hover-intent=on]/<name>:opacity-100` instead of `group-hover`
 * and stop popping at a cursor merely passing through.
 *
 * The `group/<name>` class itself stays on the caller — each surface names its
 * own group and owns its positioning.
 */
export const HoverIntentGroup = forwardRef<HTMLDivElement, HoverIntentGroupProps>(
    function HoverIntentGroup(
        { hoverIntent, innerRef, onMouseEnter, onMouseLeave, ...rest },
        forwardedRef,
    ) {
        const { hoverProps, dataProps } = useHoverIntent(hoverIntent);

        return (
            <div
                {...rest}
                {...dataProps}
                ref={(node) => {
                    setRef(forwardedRef, node);
                    setRef(innerRef, node);
                }}
                onMouseEnter={(event) => {
                    hoverProps.onMouseEnter();
                    onMouseEnter?.(event);
                }}
                onMouseLeave={(event) => {
                    hoverProps.onMouseLeave();
                    onMouseLeave?.(event);
                }}
            />
        );
    },
);
